import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api } from "@/lib/api";

const MAX_POLLS = 8;

export default function GivingSuccess() {
  usePageMeta("Thank You");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch {
        setState("error"); return;
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-20">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="max-w-lg w-full rounded-2xl bg-white border border-border p-10 text-center shadow-sm" data-testid="giving-success">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-emerald animate-spin" />
            <h1 className="mt-5 font-display text-3xl text-navy font-semibold">Confirming your gift…</h1>
            <p className="mt-2 text-muted-foreground">One moment while we confirm with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald" />
            <h1 className="mt-5 font-display text-4xl text-navy font-semibold">Thank you!</h1>
            <p className="mt-3 text-muted-foreground">
              {info?.donor_name ? `${info.donor_name}, your` : "Your"} {info?.frequency === "monthly" ? "monthly gift" : "gift"} of{" "}
              <strong className="text-navy">${((info?.amount || 0) / 100).toLocaleString()}</strong> has been received.
              A confirmation email is on its way. Your generosity helps advance God's Kingdom.
            </p>
            <img src="/jesus-dove.png" alt="" className="mx-auto mt-6 h-28 object-contain" />
            <Button asChild className="mt-6 rounded-full bg-emerald hover:bg-emerald-dark text-white px-8"><Link to="/">Return home</Link></Button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-gold" />
            <h1 className="mt-5 font-display text-3xl text-navy font-semibold">Almost there</h1>
            <p className="mt-2 text-muted-foreground">Your payment is still processing. We'll email your confirmation as soon as it clears.</p>
            <Button asChild className="mt-6 rounded-full bg-emerald hover:bg-emerald-dark text-white px-8"><Link to="/">Return home</Link></Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-ruby" />
            <h1 className="mt-5 font-display text-3xl text-navy font-semibold">We couldn't confirm your gift</h1>
            <p className="mt-2 text-muted-foreground">If you completed payment, you'll still receive a confirmation email. Otherwise, please try again.</p>
            <Button asChild className="mt-6 rounded-full bg-emerald hover:bg-emerald-dark text-white px-8"><Link to="/fund">Back to giving</Link></Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
